<template>
  <div class="document-container-wrapper">
    <div v-if="hasNavigate" class="actions-wrapper">
      <BbBtn
        size="x-small"
        type="icon"
        icon="chevron-left"
        icon-size="small"
        no-background
        class="back-button"
        @click="$emit('navigate')"
      />
      <TextBody i18n-key="collections.backToCollection" tag="span" :body-type="2" weight="medium" />
    </div>
    <GenericContentComponent v-if="visibleText" title-key="whatCanIUseFor.whatIsInside">
      <div class="document-info">
        <span v-if="visibleText">
          {{ visibleText }}
          <span v-if="hiddenText && !expanded" class="show-button" @click="expanded = true">
            <u>{{ t('common.more') }} </u></span
          >
          <span v-if="expanded">{{ hiddenText }}</span>
          <span v-if="expanded" @click="expanded = false" class="show-button"
            >&nbsp;<u>{{ t('common.less') }}</u></span
          >
        </span>
        <span v-else>No data...</span>
      </div>
    </GenericContentComponent>

    <div class="image-wrapper">
      <div class="image-box">
        <BaseImage class="logo" :src="firstPageUrl" :alt="selectedReference.name" />
        <div class="favorite-button-container">
          <CollectionButton :document="selectedReference" />
        </div>
        <BbBtn class="preview-button" size="small" type="secondary" @click="previewDocument">
          <BbIcon size="small" name="preview" type="filled" />
          <TextBody :text="previewText" />
        </BbBtn>
      </div>
    </div>

    <div class="actions">
      <BbBtn class="collection-button" size="small" type="secondary" @click="handleAddToCollection">
        <BbIcon size="small" name="collection" />
        <TextBody tag="span" i18n-key="collections.addToCollection" weight="medium" />
      </BbBtn>
      <BbBtn size="small" type="secondary" @click="openTranslateDocument">
        <BbIcon size="small" name="translate" />
        <TextBody tag="span" i18n-key="common.translate" weight="medium" />
      </BbBtn>
      <BbBtn v-if="selectedReference.sourceUrl" size="small" type="secondary" @click="openInSource">
        <BbIcon size="small" name="veeva" />
        <TextBody tag="span" i18n-key="common.open" weight="medium" />
      </BbBtn>
    </div>
    <DocumentMetadata />
  </div>
</template>

<script>
import { computed, ref, watch, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

import useModals from '~/composables/useModals';
import useDocuments from '~/composables/useDocuments';
import DocumentMetadata from '~/components/conversation/documentPanel/DocumentMetadata';
import CollectionButton from '~/components/common/CollectionButton';
import TextBody from '~/components/common/TextBody';
import BaseImage from '~/components/common/BaseImage';
import GenericContentComponent from '~/components/conversation/sidepanelGeneralComponents/GenericContentComponent.vue';
import validateUrl from '~/utils/url';
import { decodeFromBase64 } from '~/utils/message';
import { TRANSLATION_TYPE } from '~/constants/documents';

export default {
  components: { GenericContentComponent, DocumentMetadata, CollectionButton, TextBody, BaseImage },
  emits: ['navigate'],

  setup() {
    const { t } = useI18n();
    const { showTranslationModal, showPreviewDocument, setAddToCollectionPopup } = useModals();
    const { selectedReference } = useDocuments();

    const expanded = ref(false);
    const containerWidth = 442; // px
    const font = '400 16px Arial';
    const visibleText = ref('');
    const hiddenText = ref('');

    const firstPageUrl = computed(() => {
      return validateUrl(selectedReference.value.pages?.[1]?.large) ? selectedReference.value.pages?.[1]?.large : '';
    });

    const instance = getCurrentInstance();
    const hasNavigate = computed(() => {
      const props = instance?.vnode?.props || {};
      return !!(props.onNavigate || props.onnavigate || props.navigate);
    });

    const previewText = computed(
      () => `${t('common.view')} ${selectedReference.value.pageCount || ''} ${t('common.pages')}`,
    );

    const snippetText = computed(() => {
      if (selectedReference.value?.snippets) {
        const firstSnippetKey = Object.keys(selectedReference.value.snippets)[0];
        return decodeFromBase64(selectedReference.value.snippets[firstSnippetKey]);
      }
      return '';
    });

    const splitText = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      context.font = font;

      const words = snippetText.value.split(' ');
      let lines = 1; // start at line 1
      let currentLine = '';
      let visible = visibleText.value;

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine ? currentLine + ' ' + word : word;
        const metrics = context.measureText(testLine).width;

        if (metrics > containerWidth) {
          // Line is full, commit current line
          visible += currentLine + ' ';
          lines++;
          if (lines > 2) break; // stop at 2 lines
          currentLine = word; // start new line
        } else {
          currentLine = testLine;
        }
      }

      // Append last line if within 2 lines
      if (lines <= 2) {
        visible += currentLine;
      }

      visibleText.value = visible.trim();
      hiddenText.value = snippetText.value.slice(visibleText.value.length).trim();
    };

    const previewDocument = () => showPreviewDocument(1);
    const openTranslateDocument = () =>
      showTranslationModal(selectedReference.value.externalId, selectedReference.value.name, TRANSLATION_TYPE.document);
    const openInSource = () => window.open(selectedReference.value.sourceUrl || '');
    const handleAddToCollection = () => setAddToCollectionPopup({ open: true, document: selectedReference.value });

    watch(
      () => selectedReference.value.externalId,
      () => {
        expanded.value = false;
        visibleText.value = '';
        hiddenText.value = '';
        splitText();
      },
      { immediate: true, deep: true },
    );

    return {
      t,
      firstPageUrl,
      previewText,
      selectedReference,
      snippetText,
      expanded,
      previewDocument,
      openTranslateDocument,
      openInSource,
      handleAddToCollection,
      visibleText,
      hiddenText,
      hasNavigate,
    };
  },
};
</script>

<style scoped lang="scss">
.document-container-wrapper {
  overflow: auto;
  width: 100%;

  .document-info {
    color: $bb-neutral-0;
    line-height: 16px;
    font-size: 11px;
    font-weight: 400;
    font-style: normal;

    .show-button {
      cursor: pointer;
    }
  }

  .actions-wrapper {
    padding: $bb-spacing-2 $bb-spacing-4;
    background-color: $bb-neutral-0;
    width: 100%;
    display: flex;
    gap: $bb-spacing-2;
    align-items: center;

    .back-button {
      background-color: $bb-neutral-6;
      height: 36px !important;
      width: 48px !important;
      padding: 10px 16px;
      border-radius: 100px !important;
    }
  }

  .image-wrapper {
    min-height: 20%;
    object-fit: contain;
    background-color: $bb-neutral-6;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    display: flex;
    overflow: hidden;
    width: 100%;
    padding: $bb-spacing-2 $bb-spacing-4;
    margin: auto;

    .image-box {
      position: relative;
      width: 100%;

      .logo {
        aspect-ratio: auto;
        object-fit: contain;
        max-width: 328px;
        height: 264px;
        min-height: 264px;
      }

      .favorite-button-container {
        position: absolute;
        right: 6px;
        top: $bb-spacing-1;

        button {
          border-radius: 100px;
          background: $bb-neutral-0;
          height: 40px;
          width: 44px;
        }
      }

      .preview-button {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        background: rgba(255, 255, 255, 0.8);

        :deep(.v-btn__content) {
          gap: $bb-spacing-1;
        }
      }
    }
  }

  .actions {
    display: flex;
    gap: $bb-spacing-1;
    padding: $bb-spacing-2 $bb-spacing-4;
    width: 100%;
    align-items: center;
    background-color: $bb-neutral-0;

    button {
      flex-grow: 1;
    }

    .collection-button {
      background: $bb-neutral-6;
      border: none !important;
    }

    :deep(.v-btn__content) {
      gap: $bb-spacing-1;
    }
  }
}
</style>
